import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../apiClientContext";
import { useKeepAdjacentData } from "../../queryPlaceholders";
import { fetchWorkflowTimeline } from "./api";

/**
 * Query-key prefix for every workflow timeline read. Exported so the
 * realtime cache-patch layer (and any future invalidation surface) can
 * target every cached timeline page without depending on the full key
 * shape. Each cache entry is keyed by `(workflowId, entityType, entityId)`
 * triple, mirroring the workflow detail hook.
 */
export const workflowTimelineKeys = {
    all: ["workflows", "timeline"] as const,
    detail: (workflowId: string, entityType: string, entityId: string) =>
        ["workflows", "timeline", workflowId, entityType, entityId] as const,
};

export interface UseWorkflowTimelineParams {
    readonly workflowId: string;
    readonly entityType: string;
    readonly entityId: string;
}

/**
 * Read-only audit feed for the workflow Timeline tab. v1 fetches the
 * whole feed in one shot (the BFF caps event count and discloses
 * truncation in `response.truncated`); pagination can be layered on
 * later without changing the cache key shape.
 *
 * `staleTime` is generous because the audit ledger is monotonic — once
 * an event is on the timeline it never changes, so the only reason to
 * refetch is to pick up newly-emitted events. The realtime layer
 * invalidates this query directly when work-item lifecycle pushes
 * arrive, so the polling staleness is just a backstop for cases the
 * SignalR hub doesn't cover.
 */
export function useWorkflowTimeline({
    workflowId,
    entityType,
    entityId,
}: UseWorkflowTimelineParams) {
    const client = useApiClient();
    const enabled = workflowId.length > 0 && entityType.length > 0 && entityId.length > 0;
    const queryKey = workflowTimelineKeys.detail(workflowId, entityType, entityId);
    // Switching workflows while on the Timeline tab keeps the previous feed
    // on screen, dimmed, until the next one arrives.
    const placeholder = useKeepAdjacentData(queryKey, enabled);
    return useQuery({
        queryKey,
        queryFn: () => fetchWorkflowTimeline(client, workflowId, entityType, entityId),
        enabled,
        staleTime: 30_000,
        ...placeholder,
    });
}

/**
 * Warm the timeline cache before the user switches to the Timeline view.
 * Same key, same fetcher, same `staleTime` as `useWorkflowTimeline`, so
 * the switch renders from cache without a loading state. A no-op when the
 * triple is incomplete or the entry is already fresh.
 */
export function usePrefetchWorkflowTimeline({
    workflowId,
    entityType,
    entityId,
}: UseWorkflowTimelineParams): () => void {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useCallback(() => {
        if (workflowId.length === 0 || entityType.length === 0 || entityId.length === 0) return;
        void queryClient.prefetchQuery({
            queryKey: workflowTimelineKeys.detail(workflowId, entityType, entityId),
            queryFn: () => fetchWorkflowTimeline(client, workflowId, entityType, entityId),
            staleTime: 30_000,
        });
    }, [queryClient, client, workflowId, entityType, entityId]);
}
